import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiResponse } from '@/api/types'
import type { SalesReportApiResponse, SalesReportRow } from '@/modules/inventory/reports/types'

export interface SalesReportQueryParams {
  fromDate: string
  toDate: string
  pageNumber: number
  pageSize: number
  warehouseId?: number
  itemId?: number
}

function normalizeRow(raw: Record<string, unknown>): SalesReportRow {
  const g = (k: string, alt?: string) => raw[k] ?? (alt ? raw[alt] : undefined)
  return {
    date: String(g('date', 'Date') ?? ''),
    itemCode: String(g('itemCode', 'ItemCode') ?? ''),
    itemName: String(g('itemName', 'ItemName') ?? ''),
    category: String(g('category', 'Category') ?? ''),
    subCategory: String(g('subCategory', 'SubCategory') ?? ''),
    uom: String(g('uom', 'Uom') ?? ''),
    type: (g('type', 'Type') as string | null | undefined) ?? null,
    customer: String(g('customer', 'Customer') ?? ''),
    invoiceNo: String(g('invoiceNo', 'InvoiceNo') ?? ''),
    warehouse: String(g('warehouse', 'Warehouse') ?? ''),
    batchCode: String(g('batchCode', 'BatchCode') ?? ''),
    soldQuantity: Number(g('soldQuantity', 'SoldQuantity') ?? 0),
    unitCostPrice: Number(g('unitCostPrice', 'UnitCostPrice') ?? 0),
    unitSalesPrice: Number(g('unitSalesPrice', 'UnitSalesPrice') ?? 0),
    totalCostAmount: Number(g('totalCostAmount', 'TotalCostAmount') ?? 0),
    totalTaxableAmount: Number(g('totalTaxableAmount', 'TotalTaxableAmount') ?? 0),
    totalTaxAmount: Number(g('totalTaxAmount', 'TotalTaxAmount') ?? 0),
    discountAmount: Number(g('discountAmount', 'DiscountAmount') ?? 0),
    totalAmountWithTax: Number(g('totalAmountWithTax', 'TotalAmountWithTax') ?? 0),
  }
}

function extractPayload(responseData: unknown, params: SalesReportQueryParams): SalesReportApiResponse {
  const fallback = (): SalesReportApiResponse => ({
    totalRecords: 0,
    pageNumber: params.pageNumber,
    pageSize: params.pageSize,
    data: [],
  })

  if (!responseData || typeof responseData !== 'object') {
    return fallback()
  }

  const root = responseData as Record<string, unknown>

  const inner =
    'data' in root &&
    root.data &&
    typeof root.data === 'object' &&
    !Array.isArray(root.data) &&
    'data' in (root.data as object)
      ? (root.data as Record<string, unknown>)
      : root

  const rowsRaw = inner.data
  const data: SalesReportRow[] = Array.isArray(rowsRaw)
    ? rowsRaw.map((r) => normalizeRow(r as Record<string, unknown>))
    : []

  const totalRecords = Number(inner.totalRecords ?? inner.TotalRecords ?? data.length) || 0
  const pageNumber = Number(inner.pageNumber ?? inner.PageNumber ?? params.pageNumber) || params.pageNumber
  const pageSize = Number(inner.pageSize ?? inner.PageSize ?? params.pageSize) || params.pageSize

  return { totalRecords, pageNumber, pageSize, data }
}

export class SalesReportService {
  async getSalesReport(params: SalesReportQueryParams): Promise<SalesReportApiResponse> {
    const url = endpoints.reports.salesReport({
      fromDate: params.fromDate,
      toDate: params.toDate,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
      warehouseId: params.warehouseId,
      itemId: params.itemId,
    })
    const response = await apiClient.get<
      ApiResponse<SalesReportApiResponse> | SalesReportApiResponse | SalesReportRow[]
    >(url)

    const d = response.data

    if (Array.isArray(d)) {
      return {
        data: d.map((r) => normalizeRow(r as unknown as Record<string, unknown>)),
        totalRecords: d.length,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
      }
    }

    if (d && typeof d === 'object' && 'data' in d && 'totalRecords' in d) {
      return extractPayload(d, params)
    }

    if (d && typeof d === 'object' && 'success' in d && 'data' in d) {
      const wrapped = d as ApiResponse<SalesReportApiResponse>
      return extractPayload(wrapped.data, params)
    }

    return extractPayload(d, params)
  }
}

export const salesReportService = new SalesReportService()
